import { Application } from 'pixi.js';
import { World, type MapData, type ZoneData } from './game/World';
import { PlayerEntity } from './game/PlayerEntity';
import { HUD } from './game/HUD';
import { InputHandler } from './game/InputHandler';

// ── Demo constants ────────────────────────────────────────────────────────────
const TILE_SIZE   = 64;
const MAP_COLS    = 10;
const MAP_ROWS    = 10;
const MAP_W       = MAP_COLS * TILE_SIZE;
const MAP_H       = MAP_ROWS * TILE_SIZE;
const MOVE_SPEED  = 3;

// ── Build demo map (10×10) ────────────────────────────────────────────────────
function buildDemoMap(): MapData {
  const tiles: MapData['tiles'] = [];
  for (let r = 0; r < MAP_ROWS; r++) {
    tiles[r] = [];
    for (let c = 0; c < MAP_COLS; c++) {
      const isEdge  = r === 0 || r === MAP_ROWS - 1 || c === 0 || c === MAP_COLS - 1;
      const isDirt  = (r * 3 + c * 7) % 6 === 0;
      const isWater = r === 3 && c >= 3 && c <= 5;
      tiles[r][c] = {
        type: isWater ? 'water' : isEdge ? 'stone' : isDirt ? 'dirt' : 'grass',
      };
    }
  }
  return { cols: MAP_COLS, rows: MAP_ROWS, tileSize: TILE_SIZE, tiles };
}

// ── Main ──────────────────────────────────────────────────────────────────────
async function init() {
  // ── Pixi App ─────────────────────────────────────────────────────────────
  const app = new Application();
  await app.init({
    width:           800,
    height:          600,
    background:      0x1a1a2e,
    antialias:       true,
    resolution:      window.devicePixelRatio || 1,
    autoDensity:     true,
  });

  const root = document.getElementById('app');
  if (!root) throw new Error('#app not found');
  root.appendChild(app.canvas);

  // ── World ─────────────────────────────────────────────────────────────────
  const world = new World(app);

  // Draw 10×10 map
  const map = buildDemoMap();
  world.drawMap(map);

  // Draw zone (slightly smaller than map, centred)
  const zone: ZoneData = {
    cx:     MAP_W / 2,
    cy:     MAP_H / 2,
    radius: 240,
  };
  world.drawZone(zone);

  // ── Local player at map centre ────────────────────────────────────────────
  const startX = MAP_W / 2;
  const startY = MAP_H / 2;

  const player = new PlayerEntity(
    'local-demo',
    'You',
    { hat: 'hat-crown' },
    startX,
    startY,
  );
  await player.loadAvatar();
  player.setHp(75, 100);
  world.playerContainer.addChild(player);

  // Start idle bob on the avatar (access via the Avatar child)
  // Avatar's playIdleAnimation is internal; we bob via a simple ticker offset instead
  world.snapCamera(startX, startY);

  // ── HUD ───────────────────────────────────────────────────────────────────
  const hud = new HUD(app, { maxHp: 100 });
  hud.setHp(75);
  hud.setKills(3);
  hud.setAliveCount(42);
  // Zone warning hidden by default — will show when player is outside zone
  app.stage.addChild(hud);

  // ── Input ─────────────────────────────────────────────────────────────────
  const input = new InputHandler(app.canvas as HTMLCanvasElement);

  // ── Game loop ─────────────────────────────────────────────────────────────
  let bobTime = 0;

  app.ticker.add((ticker) => {
    const deltaMS = ticker.deltaMS;
    bobTime += deltaMS;

    // Read input
    const state = input.getState();

    // Move local player
    if (state.dx !== 0 || state.dy !== 0) {
      player.x += state.dx * MOVE_SPEED;
      player.y += state.dy * MOVE_SPEED;

      // Clamp to map bounds
      player.x = Math.max(0, Math.min(MAP_W, player.x));
      player.y = Math.max(0, Math.min(MAP_H, player.y));
    }

    // Idle bob (offset the player's Y slightly for visual life)
    const bobOffset = Math.sin(bobTime / 800) * 2.5;
    player.y += bobOffset * 0.016; // tiny per-frame nudge (dampened)

    // Aim
    player.aimAt(state.aimAngle);

    // Pan camera to follow player
    world.panCamera(player.x, player.y, 0.1);

    // Zone warning
    const dist = Math.hypot(player.x - zone.cx, player.y - zone.cy);
    hud.setZoneWarning(dist > zone.radius);

    // Minimap — just the local player dot for the demo
    hud.updateMinimap([], MAP_W, MAP_H, { x: player.x, y: player.y });
  });

  console.log('Stiffy Royale — World renderer demo running!');
  console.log('WASD to move, mouse to aim, click to shoot');
}

init().catch(console.error);
